import React, { useCallback, useContext } from 'react';
import { noop } from '@moda/portal-stanchions';
import { VariantCell } from '../../../Grid';
import { CuratedLook, CuratedLookItem } from '../../curatedLooks.rest';
import { VariantCellFragment } from '../../../../generated/types';
import { getActiveVariantBy } from '../../../../lib/getActiveVariantBy';

type ImageUrl = {
  src: string;
  angle: string | null;
};

const CuratedLookVariantCellContext = React.createContext({
  isCuratedLookItem: false,
  mainLookId: undefined as undefined | number,
  itemSize: undefined as undefined | string,
  itemColor: undefined as undefined | string,
  itemQty: undefined as undefined | number,
  itemId: undefined as undefined | number,
  itemImageKey: null as null | string,
  getInitialAngle: noop as (activeVariantId: string) => string | undefined
});

interface Props {
  item: CuratedLookItem;
  look: CuratedLook;
  variant: undefined | VariantCellFragment;
}

export const CuratedLookVariantCell: React.FC<Props> = ({
  look,
  item: { imageKey, color, size, quantity, id },
  variant
}) => {
  // Runs only for Curated Look items with imageKey
  // otherwise returns undefined so the gallery defaults to the primary image
  const getInitialAngle = useCallback(
    (activeVariantId: string) => {
      if (!imageKey) return undefined;

      if (!variant) return undefined;

      const activeVariant = getActiveVariantBy(variant, 'id', activeVariantId);

      const { primaryImageUrls, alternateImageUrls } = activeVariant;

      if (primaryImageUrls?.angle === imageKey) return imageKey;

      const altByAngle = (alternateImageUrls || []).find(
        (img: ImageUrl) => img?.angle === imageKey
      );

      if (altByAngle) return imageKey;

      const all = [primaryImageUrls, ...(alternateImageUrls || [])].filter(Boolean);

      const byKey = all.find((img: ImageUrl) => img?.src?.includes(imageKey));

      return byKey?.angle ?? undefined;
    },
    [imageKey, variant]
  );

  if (!variant) return null;

  return (
    <CuratedLookVariantCellContext.Provider
      value={{
        isCuratedLookItem: true,
        mainLookId: look.id,
        itemColor: color,
        itemSize: size,
        itemQty: quantity,
        itemId: id,
        itemImageKey: imageKey,
        getInitialAngle
      }}
    >
      <VariantCell className="CuratedLook__item" key={id} variant={variant} />
    </CuratedLookVariantCellContext.Provider>
  );
};

export const useCuratedLookContextOnVariantCell = () => useContext(CuratedLookVariantCellContext);
